import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type {
  ErasureProgress,
  InitiateErasureInput,
  OpenAccountInput,
  Preferences,
  Profile,
  RecordErasureStepFailedInput,
  SettleErasureNotifyInput,
  SignInLinkProvider,
  SearchUsersInput,
  UpdateProfileInput,
  UploadAvatarInput,
  User,
} from "./account.schemas.ts";
import type { PageUsersInput, UserActivity, UserPage } from "./directory.schemas.ts";

// What account promises: the signed-in person's profile, preferences and
// ways to sign in. The plan, credits, limits and features belong to the
// organization now (ADR-003) and live in modules/billing.

export type ForUser = { readonly userId: string };

export interface AccountApi extends EraseStep {
  /** Signing up: a fresh person. */
  open(input: OpenAccountInput): Promise<Profile>;
  /** Signing in: who owns this email, if anyone. */
  findUserIdByEmail(input: { readonly email: string }): Promise<string | null>;
  profile(input: ForUser): Promise<Profile>;
  updateProfile(input: ForUser & UpdateProfileInput): Promise<Profile>;
  /** Replaces the avatar, checked and decoded by `@amtana/media` first (S6.03). */
  uploadAvatar(input: ForUser & UploadAvatarInput): Promise<Profile>;
  /** Removes the avatar; the initials show again everywhere it was used. */
  removeAvatar(input: ForUser): Promise<Profile>;
  updatePreferences(input: ForUser & Preferences): Promise<Profile>;
  /** Whether a sign-in provider (Google, LinkedIn) is linked, as My account shows it. Peer-only:
   * auth sets it when a link is made or removed (auth ADR-007 point 5); never on the wire. */
  setSignInLink(
    input: ForUser & { readonly provider: SignInLinkProvider; readonly connected: boolean },
  ): Promise<Profile>;
  /** Every account's person, for organization's one-time personal-organization backfill
   * (S1.02). Peer-only; never on the wire. */
  listAllUsers(): Promise<readonly User[]>;
  /** The people behind a set of ids, for organization's members directory (S3.02) to
   * show a name and email beside each role. Peer-only; never on the wire. Skips an id
   * with no account rather than refusing. */
  findUsersByIds(input: { readonly userIds: readonly string[] }): Promise<readonly User[]>;
  /** The back office's own people search (S9.04, S12.04, ADR-005 point 4): every account whose
   * email or full name contains the query, case-insensitive. Peer-only; never on the wire. */
  searchUsers(input: SearchUsersInput): Promise<readonly User[]>;
  /** The back office's people directory (ADR-033): one keyset page, sorted and filtered by
   * account itself over its own table. Peer-only; never on the wire. */
  pageUsers(input: PageUsersInput): Promise<UserPage>;
  /** Auth's report that this person made an authenticated request or refreshed a session at
   * `at` (ADR-033 point 2). Written only when the stored moment is older than half of
   * `LAST_SEEN_EVERY_MS`, so a busy person costs one write every few minutes. Peer-only. */
  recordSeen(input: ForUser & { readonly at: string }): Promise<void>;
  /** When this person's account was opened and when they were last seen, for their back-office
   * record. Both null for an account that no longer exists. Peer-only. */
  activity(input: ForUser): Promise<UserActivity>;
  /** The person's own saved language (S6.04/S6.05): read by a transactional email's
   * sender (auth's verification and reset mail, notification's own) to mail the
   * recipient in their own language. Peer-only; never on the wire, since account
   * already serves the full preference through `profile`. */
  language(input: ForUser): Promise<Preferences["language"]>;
  /** Auth's own proof of this address changing, kept as account's copy (S3.05): called
   * once a verification link is spent, once SSO admits an address, or once an SSO
   * provider proves an address auth already had unverified. Peer-only; never on the
   * wire, since account owns no verification flow of its own. */
  setEmailVerified(input: ForUser & { readonly verified: boolean }): Promise<void>;
  /** Auth's own swap of the sign-in address, kept as account's copy (S6.02): called once a
   * mailed confirmation link is spent. Peer-only; never on the wire, since account owns no
   * email-change flow of its own. Refuses with `AccountEmailTakenError` when another account
   * already has this email. */
  setEmail(input: ForUser & { readonly email: string }): Promise<void>;
  /** Auth's own orchestration of account deletion (S6.08): starts the grace period, once
   * the last-admin check has already passed. `organizationIds` (D879-D881, billing ordering fix):
   * the person's sole/personal organization ids, computed by `ErasureGateService` at the same
   * gate, before anything is purged anywhere; persisted on the row's own `deletion` field and
   * read back by `initiateErasure` once the grace period elapses, so it reaches every module's
   * own erase-step intent the same way the still-live email already does (D828), regardless of
   * whether organization's own row has since been purged. Peer-only; never on the wire. */
  requestDeletion(input: ForUser & { readonly organizationIds?: readonly string[] }): Promise<{
    purgeAt: string;
  }>;
  /** Clears a pending deletion; a no-op when none is pending. Auth calls this on every
   * successful sign-in (S6.08, Decided in spec), so signing in during the grace period is
   * itself how an account is restored. Answers whether anything was actually pending, so
   * auth knows whether to restore the personal organization deleting the account took with
   * it too (S6.10, D307); a no-op answers `false`. Peer-only; never on the wire. */
  cancelDeletionIfPending(input: ForUser): Promise<boolean>;
  /** Every self-service account whose grace period has elapsed and whose gate has not yet
   * cleared, for auth's scheduled task (ADR-026, S30.01). Peer-only. */
  accountsPastGracePeriod(input: { readonly now: string }): Promise<readonly ForUser[]>;
  /** The gate clearing for real (ADR-026 point 3, epic 30, S30.01): self-service once the
   * grace period elapses, staff immediately, no grace at all (D805). States
   * `account.erasure_initiated` before anything is purged anywhere, which the Go `erasure`
   * process manager hears to fan one `erase-step` worker-bridge intent out per module, once
   * S30.13's own confirmation delivery settles. Peer-only; never on the wire. */
  initiateErasure(input: ForUser & InitiateErasureInput): Promise<void>;
  /** This person's own erasure progress, for `/ops` (ADR-026 point 3). Peer-only. */
  erasureProgress(input: ForUser): Promise<ErasureProgress>;
  /** States `account.erasure_step_failed` under the dead erase-step intent's key, for
   * worker-bridge's `erasure/pm/fanout` manager (epic 44, S44.07, D1392): only account may
   * append account's facts. A repeated key states nothing new. Peer-only; never on the wire. */
  recordErasureStepFailed(input: RecordErasureStepFailedInput): Promise<void>;
  /** States `account.erasure_notify_settled` under the settling intent's key, for worker-bridge's
   * `erasure/pm/notify` manager (S44.07, D1392); with `lapsed`, `erasure_step_failed` naming
   * "notify" first, in the same append. A repeated key states nothing new. Peer-only. */
  settleErasureNotify(input: SettleErasureNotifyInput): Promise<void>;
}

export const AccountApi = moduleApi<AccountApi>()("account");
